// Upload state machine for the presigned R2 flow (docs/03 §media):
//   POST /v1/media  →  PUT upload_url (with progress)  →  POST /media/:id/complete
// Kept dependency-injected and framework-free so it is unit-testable without a
// real network or React. The Media page drives it and mirrors phase → UI.

import type { CreateMedia, Media } from '@seencast/shared';

export type UploadPhase =
  | 'queued'
  | 'creating' // requesting presigned URL
  | 'uploading' // PUT to R2
  | 'completing' // confirming → server extracts dims
  | 'done'
  | 'error'
  | 'canceled';

export interface UploadItem {
  id: string; // local id (not the media id)
  filename: string;
  size_bytes: number;
  kind: 'image' | 'video';
  phase: UploadPhase;
  progress: number; // 0..1, meaningful during 'uploading'
  mediaId?: string;
  error?: string;
}

/** Reject files we can't take; the UI shows the reason inline. */
export function inferMediaKind(file: { type: string }): 'image' | 'video' | null {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return null;
}

export const isTerminal = (p: UploadPhase): boolean =>
  p === 'done' || p === 'error' || p === 'canceled';

export interface UploadDeps {
  createMedia(body: CreateMedia): Promise<{ media_id: string; upload_url: string }>;
  uploadToUrl(
    url: string,
    blob: Blob,
    onProgress: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  completeMedia(id: string): Promise<Media>;
  // Thumbnail generation + upload (docs/10 #2.2) — both optional so existing callers/tests
  // that only care about the core upload flow don't need to stub them. Best-effort: never
  // awaited by the caller and any failure is swallowed, see the call site below.
  generateThumb?(file: File, kind: 'image' | 'video'): Promise<Blob | null>;
  putThumb?(mediaId: string, blob: Blob): Promise<void>;
}

export interface PerformUploadParams {
  file: File;
  kind: 'image' | 'video';
  deps: UploadDeps;
  onPhase: (patch: { phase: UploadPhase; progress?: number; mediaId?: string; error?: string }) => void;
  signal?: AbortSignal;
}

/**
 * Run one file through the full flow, reporting each transition via `onPhase`.
 * Resolves to the created media id, or throws (after reporting phase='error').
 * Abort via `signal` reports phase='canceled' and rethrows.
 */
export async function performUpload(params: PerformUploadParams): Promise<string> {
  const { file, kind, deps, onPhase, signal } = params;
  try {
    onPhase({ phase: 'creating', progress: 0 });
    const { media_id, upload_url } = await deps.createMedia({
      filename: file.name,
      mime: file.type,
      size_bytes: file.size,
      kind,
    });
    onPhase({ phase: 'uploading', progress: 0, mediaId: media_id });

    await deps.uploadToUrl(upload_url, file, (f) => onPhase({ phase: 'uploading', progress: f }), signal);

    onPhase({ phase: 'completing', progress: 1, mediaId: media_id });
    await deps.completeMedia(media_id);

    onPhase({ phase: 'done', progress: 1, mediaId: media_id });

    // Fire-and-forget: a thumb is a nice-to-have. It must never delay 'done' or turn
    // a successful upload into an error, so it's neither awaited nor allowed to reject.
    if (deps.generateThumb && deps.putThumb) {
      void deps
        .generateThumb(file, kind)
        .then((blob) => (blob ? deps.putThumb!(media_id, blob) : undefined))
        .catch(() => {
          /* best-effort — no UI feedback on thumb failure */
        });
    }

    return media_id;
  } catch (err) {
    if (signal?.aborted) {
      onPhase({ phase: 'canceled' });
      throw err;
    }
    onPhase({ phase: 'error', error: err instanceof Error ? err.message : 'Upload failed' });
    throw err;
  }
}
